import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Phone, MessageSquare, ExternalLink, Plus, X, Heart, Printer, Loader2 } from "lucide-react";
import { api, local, downloadFile, errMsg } from "@/lib/api";
import { PageHeader, SectionTitle, Tabs } from "@/components/kit";
import { WalletCardDialog } from "@/components/safety/WalletCardDialog";
import { Hotline } from "@/components/safety/Hotline";
import { WifiOff } from "lucide-react";
import { US_HOTLINES, CANADA_HOTLINES, TWELVE_STEP_GROUPS, ALTERNATIVE_GROUPS, PROFESSIONAL_HELP, AFFIRMATIONS, DEFAULT_SAFETY_PLAN } from "@/data/safety";

const PLAN_SECTIONS = [
  ["warningSigns", "⚠️ My warning signs"], ["emotionalTriggers", "💭 Emotional triggers"], ["internalStrategies", "🧘 Things I can do on my own"],
  ["socialDistractors", "👥 People & places that distract me"], ["trustedContacts", "📞 People I can ask for help"],
  ["professionalContacts", "🩺 Professionals I can contact"], ["safeEnvironmentSteps", "🏠 Making my environment safe"],
  ["safePlaces", "📍 Safe places I can go"], ["reasonsToLive", "💜 My reasons to live"],
];

const PLAN_CACHE = "mm_safety_plan_cache";
export const cachePlan = (plan) => {
  const sections = PLAN_SECTIONS.map(([k, t]) => ({ title: t, items: plan[k] || [] })).filter((x) => x.items.length);
  localStorage.setItem(PLAN_CACHE, JSON.stringify({ saved_at: new Date().toISOString(), plan, sections }));
};
export const readCachedPlan = () => { try { return JSON.parse(localStorage.getItem(PLAN_CACHE) || "null"); } catch { return null; } };

const ListEditor = ({ k, title, items, onChange, readOnly }) => {
  const [v, setV] = useState("");
  const add = () => { if (v.trim()) { onChange([...items, v.trim()]); setV(""); } };
  return (
    <div className="mm-card p-5" data-testid={`safety-section-${k}`}>
      <h3 className="font-semibold mb-3">{title}</h3>
      <div className="flex flex-wrap gap-2 mb-3">
        {items.map((it, i) => <span key={`${it}-${i}`} className="mm-chip !cursor-default">{it}{!readOnly && <button type="button" className="p-1 -mr-1 rounded-full hover:bg-mm-soft" aria-label="Remove" onClick={(e) => { e.stopPropagation(); onChange(items.filter((_, j) => j !== i)); }} data-testid={`safety-remove-${k}-${i}`}><X size={16} /></button>}</span>)}
        {!items.length && <span className="text-sm text-mm-muted">Nothing added yet</span>}
      </div>
      {!readOnly && <div className="flex gap-2">
        <input className="mm-input !py-2" value={v} onChange={(e) => setV(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} placeholder="Add…" data-testid={`safety-input-${k}`} />
        <button className="mm-btn-ghost !px-3" onClick={add} data-testid={`safety-add-${k}`}><Plus size={16} /></button>
      </div>}
    </div>
  );
};

const LinkList = ({ items }) => (
  <div className="grid sm:grid-cols-2 gap-3">
    {items.map((g) => <a key={g.name} href={g.url} target="_blank" rel="noreferrer" className="mm-card mm-card-hover p-4 flex justify-between items-center"><div><div className="font-semibold text-sm">{g.name}</div><div className="text-xs text-mm-muted">{g.description}</div></div><ExternalLink size={16} className="text-mm-muted" /></a>)}
  </div>
);

const SAFETY_TABS = [{ id: "crisis", name: "Crisis lines" }, { id: "plan", name: "My safety plan" }, { id: "support", name: "Support groups" }];
const COUNTRIES = [["us", "🇺🇸 United States"], ["ca", "🇨🇦 Canada"]];
const LINES = { us: US_HOTLINES, ca: CANADA_HOTLINES };
const PIN_KEY = "mm_crisis_pin";

const useOnline = () => {
  const [on, setOn] = useState(navigator.onLine);
  useEffect(() => {
    const up = () => setOn(true), down = () => setOn(false);
    window.addEventListener("online", up); window.addEventListener("offline", down);
    return () => { window.removeEventListener("online", up); window.removeEventListener("offline", down); };
  }, []);
  return on;
};

const savePinLocal = (pin) => {
  const h = pin && LINES[pin.country]?.find((l) => l.name === pin.name);
  if (!h) { localStorage.removeItem(PIN_KEY); localStorage.removeItem("mm_crisis_pin_line"); return; }
  localStorage.setItem(PIN_KEY, JSON.stringify(pin));
  localStorage.setItem("mm_crisis_pin_line", JSON.stringify({ name: h.name, call: h.canCall ? h.number : null, text: h.canText ? h.textNumber || h.number : null }));
};

const usePin = () => {
  const [pin, setPin] = useState(() => { try { return JSON.parse(localStorage.getItem(PIN_KEY) || "null"); } catch { return null; } });
  useEffect(() => {
    api.get("/safety/crisis-pin").then((r) => { setPin(r.data.pin); savePinLocal(r.data.pin); }).catch(() => {});
  }, []);
  const choose = async (country, h) => {
    const next = h ? { country, name: h.name } : null;
    const prev = pin;
    setPin(next); savePinLocal(next);
    try { await api.put("/safety/crisis-pin", next || { name: null }); toast.success(h ? `${h.name} pinned as your line` : "Unpinned"); }
    catch { setPin(prev); savePinLocal(prev); toast.error("Could not save — check your connection"); }
  };
  return [pin, choose];
};

const CrisisLines = ({ country }) => {
  const [pin, choose] = usePin();
  const pinned = pin && LINES[pin.country]?.find((l) => l.name === pin.name);
  const others = useMemo(() => LINES[country].filter((h) => h !== pinned), [country, pinned]);
  return (
    <div className="space-y-3">
      {pinned && <Hotline h={pinned} pinned onPin={() => choose(pin.country, null)} />}
      {others.map((h) => <Hotline key={h.name} h={h} onPin={(x) => choose(country, x)} />)}
    </div>
  );
};

export default function Safety() {
  const [tab, setTab] = useState(() => (new URLSearchParams(window.location.search).get("tab") === "plan" ? "plan" : "crisis"));
  const [cachedAt, setCachedAt] = useState(null);
  const [country, setCountry] = useState("us");
  const [plan, setPlan] = useState(DEFAULT_SAFETY_PLAN);
  const [aff] = useState(AFFIRMATIONS[Math.floor(Math.random() * AFFIRMATIONS.length)]);
  const online = useOnline();

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/safety/full-plan");
      const legacy = local.get("safety_plan", null);
      let merged = { ...DEFAULT_SAFETY_PLAN, ...data.plan };
      if (legacy && !Object.values(data.plan).some((v) => v?.length)) {
        merged = { ...merged, ...legacy };
        await api.put("/safety/full-plan", { plan: merged });
      }
      if (legacy) localStorage.removeItem("safety_plan");
      setPlan(merged);
      cachePlan(merged);
    })().catch((e) => {
      const c = !e?.response && readCachedPlan();
      if (c) { setPlan({ ...DEFAULT_SAFETY_PLAN, ...c.plan }); setCachedAt(c.saved_at); return; }
      if (navigator.onLine) toast.error("Could not load your safety plan");
    });
  }, []);

  const [pdfBusy, setPdfBusy] = useState(false);
  const downloadPdf = async () => {
    setPdfBusy(true);
    try { await downloadFile("/safety/full-plan/pdf", "my-safety-plan.pdf"); toast.success("Safety plan PDF downloaded"); }
    catch (e) { toast.error(errMsg(e, "Could not create PDF")); }
    finally { setPdfBusy(false); }
  };

  const update = (k) => async (v) => {
    const n = { ...plan, [k]: v };
    setPlan(n);
    try { await api.put("/safety/full-plan", { plan: n }); cachePlan(n); } catch { toast.error("Could not save — check your connection"); }
  };
  const check = (safe) => {
    const list = local.get("safety_checks", []);
    local.set("safety_checks", [{ date: new Date().toISOString(), safe }, ...list]);
    toast[safe ? "success" : "warning"](safe ? "Thank you for checking in 💜" : "Please reach out — call or text 988 now. You are not alone.");
    if (!safe) setTab("crisis");
  };

  return (
    <div data-testid="safety-page">
      <PageHeader back title="Safety" subtitle="Your plan, crisis lines and support — always here" />
      {!online && (
        <div className="mm-soft p-3 mb-4 flex items-center gap-2 text-sm" role="status" data-testid="safety-offline-note">
          <WifiOff size={16} className="text-mm-primary" /> You're offline — crisis numbers still work.
        </div>
      )}
      <div className="rounded-3xl p-6 text-white mb-6" style={{ background: "linear-gradient(135deg, #E85D75, #C2185B)" }}>
        <div className="font-bold text-lg">In immediate danger? Call 911 or call/text 988.</div>
        <p className="text-sm opacity-90 mt-1">Mindful Me is not a crisis service. You deserve support right now.</p>
        <div className="flex flex-wrap gap-2 mt-4">
          <a href="tel:988" className="bg-white text-[#C2185B] font-bold rounded-full px-5 py-2.5 inline-flex items-center gap-2 mm-pulse-ring" data-testid="safety-call-988"><Phone size={16} />Call 988</a>
          <a href="sms:988" className="bg-white/20 font-semibold rounded-full px-5 py-2.5 inline-flex items-center gap-2" data-testid="safety-text-988"><MessageSquare size={16} />Text 988</a>
        </div>
      </div>
      <div className="mm-card p-5 mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div><div className="font-semibold">Daily safety check</div><div className="text-sm text-mm-muted flex items-center gap-1"><Heart size={14} className="text-mm-error" /> {aff}</div></div>
        <div className="flex gap-2"><button className="mm-btn" onClick={() => check(true)} data-testid="safety-check-safe">I'm safe</button><button className="mm-btn mm-btn-danger" onClick={() => check(false)} data-testid="safety-check-unsafe">I need help</button></div>
      </div>
      <Tabs items={SAFETY_TABS} value={tab} onChange={setTab} testPrefix="safety-tab" />
      {tab === "crisis" && (
        <>
          <div className="flex gap-2 mb-4">{COUNTRIES.map(([id, l]) => <button key={id} onClick={() => setCountry(id)} className={`mm-chip ${country === id ? "mm-chip-on" : ""}`} data-testid={`safety-country-${id}`}>{l}</button>)}</div>
          <CrisisLines country={country} />
        </>
      )}
      {tab === "plan" && (
        <>
          <div className="flex flex-wrap justify-end gap-2 mb-4">
            <WalletCardDialog />
            <button className="mm-btn-ghost" onClick={downloadPdf} disabled={pdfBusy} data-testid="safety-download-pdf">
              {pdfBusy ? <Loader2 size={16} className="animate-spin" /> : <Printer size={16} />} Download printable PDF
            </button>
          </div>
          {cachedAt && (
            <div className="mm-soft p-3 mb-4 text-sm" role="status" data-testid="safety-plan-offline-note">
              Offline copy saved on this device · last updated {new Date(cachedAt).toLocaleString()}. Read-only until you're back online.
            </div>
          )}
          <div className="grid md:grid-cols-2 gap-4">{PLAN_SECTIONS.map(([k, t]) => <ListEditor key={k} k={k} title={t} items={plan[k] || []} onChange={update(k)} readOnly={!!cachedAt} />)}</div>
        </>
      )}
      {tab === "support" && (
        <>
          <SectionTitle>Professional help</SectionTitle><LinkList items={PROFESSIONAL_HELP} />
          <SectionTitle>12-step groups</SectionTitle><LinkList items={TWELVE_STEP_GROUPS} />
          <SectionTitle>Alternative recovery</SectionTitle><LinkList items={ALTERNATIVE_GROUPS} />
        </>
      )}
    </div>
  );
}
